/**
 * claude-regional-fixes (issue 3) — types and helpers for the per-region setup
 * of a parallel multi-region submission. Kept separate from the component file
 * so React Fast Refresh (and the eslint rule that guards it) stays happy.
 */
import { fdaActivityStartSequenceError } from '@shared/fdaActivityStartSequence';
import { initialSequenceNumberForRegion } from '@shared/initialSequence';
import { applicationIdentifierSpec } from '@shared/applicationIdentity';
import type { SubmissionTypeGroup } from '../../api/v2Client';

/**
 * Which market identifier a region carries, and how to label it. The region →
 * field/label decision is NOT owned here: it delegates to the one canonical
 * evaluator (`applicationIdentifierSpec`) so the wizard and the backend can
 * never diverge on what identifies an application. A control/transaction number
 * never identifies an application, so an unknown region resolves to `null` —
 * we do NOT guess a ControlNumber field for it.
 */
export interface RegionIdentifierSpec {
  readonly key: 'NdaNumber' | 'DossierId' | 'EuApplicationUuid';
  readonly label: string;
}

export function identifierSpecForRegion(region: string): RegionIdentifierSpec | null {
  const spec = applicationIdentifierSpec(region);
  return spec ? { key: spec.field, label: spec.label } : null;
}

/** The per-region draft the wizard holds (controlled by the parent). */
export interface ParallelRegionDraft {
  fdaActivityStartSequence?: string;
  applicationType: string;
  submissionTypeCode: string;
  submissionSubTypeCode: string;
  identifier: string;
}

export function emptyRegionDraft(): ParallelRegionDraft {
  return { applicationType: '', submissionTypeCode: '', submissionSubTypeCode: '', identifier: '' };
}

/**
 * Is a draft complete enough to submit? A sub-type is required only when the
 * selected activity's catalog group actually offers sub-types (mirrors the
 * server's "mandatory sub-type" rule).
 */
export function isRegionDraftComplete(
  draft: ParallelRegionDraft,
  groups: SubmissionTypeGroup[] | undefined,
): boolean {
  if (!draft.applicationType || !draft.submissionTypeCode || draft.identifier.trim() === '') {
    return false;
  }
  const group = groups?.find((g) => g.code === draft.submissionTypeCode);
  if (group && group.subTypes.length > 0 && draft.submissionSubTypeCode === '') return false;
  return true;
}

/** Additional regional applications start at their own canonical initial sequence. */
export function parallelActivityStartError(region: string, draft: ParallelRegionDraft | undefined): string | null {
  return region === 'FDA'
    ? fdaActivityStartSequenceError(draft?.fdaActivityStartSequence, initialSequenceNumberForRegion(region))
    : null;
}
